/**
 * FmTableCell — the one rendering path for structured table cells.
 *
 * Species swatches, operation tags, and profit/loss tones belong here, not in
 * route-local cell components such as OpsRow, so every archetype table —
 * operational, report, gallery —
 * gets the same active ledger, not a white text grid.
 */
import React from 'react';
import type { FmDataDisplayRule, FmDataValueTone } from '../../types/fm-data-value';
import type {
  FmTableOperationKind,
  FmTableSignedFormat,
  FmTableSignedTone,
  FmTableStructuredCell,
} from '../../types/fm-table';
import { ColorSwatch } from './ColorSwatch';
import { FmInline, FmText } from './FmComposition';
import { FmDataValue } from './FmDataValue';
import { FmTag } from './FmTag';
import { paletteSpecies } from './charts/palette';
import { localeToCurrency } from '@/utils/plannerFormatters';

/**
 * Quiet tag hues for the table surface — the grayed semantic accents, not the
 * saturated map operation palette. Same contract as the data-table preview and
 * the operations ledger: tags stay legible on the hairline grid.
 */
const OPERATION_TAG_HUE: Record<FmTableOperationKind, string> = {
  thinning: 'var(--fm-sky)',
  'seed-tree': 'var(--fm-amber)',
  clearcut: 'var(--fm-rose)',
  planting: 'var(--fm-teal)',
  tending: 'var(--fm-accent)',
  other: 'var(--fm-text-muted)',
};

const SIGNED_TONE: Record<FmTableSignedTone, FmDataValueTone> = {
  default: 'neutral',
  positive: 'positive',
  negative: 'negative',
  subtle: 'muted',
};

export function isFmTableStructuredCell(value: unknown): value is FmTableStructuredCell {
  if (typeof value !== 'object' || value === null || !('kind' in value)) return false;
  const kind = (value as { kind: unknown }).kind;
  return kind === 'signed' || kind === 'species' || kind === 'operation';
}

function signedRule(
  cell: Extract<FmTableStructuredCell, { kind: 'signed' }>,
  locale: string | undefined,
): FmDataDisplayRule {
  const tone = SIGNED_TONE[cell.tone];
  if (cell.value === null) return { kind: 'unavailable', tone: 'muted' };
  const format: FmTableSignedFormat =
    cell.format ?? (typeof cell.value === 'string' ? 'text' : 'currency');
  if (typeof cell.value === 'string' || format === 'text') {
    return { kind: 'text', value: cell.value, tone, emphasis: cell.emphasis };
  }
  if (format === 'volume') {
    return {
      kind: 'volume',
      value: cell.value,
      unit: 'm³',
      precision: cell.precision ?? 0,
      tone,
      emphasis: cell.emphasis,
    };
  }
  if (format === 'number') {
    return {
      kind: 'number',
      value: cell.value,
      precision: cell.precision ?? 0,
      tone,
      emphasis: cell.emphasis,
    };
  }
  return {
    kind: 'currency',
    value: cell.value,
    currency: locale ? localeToCurrency(locale) : 'EUR',
    precision: cell.precision ?? 0,
    tone,
    signDisplay: 'except-zero',
    emphasis: cell.emphasis,
  };
}

/** A missing value reads as one em dash; the empty label is its accessible name. */
function missingCell(emptyLabel: string): React.ReactElement {
  return (
    <span className="fm-table__empty-cell" aria-label={emptyLabel || undefined}>
      {'\u2014'}
    </span>
  );
}

export function renderFmTableCell(
  cell: unknown,
  options: { emptyLabel: string; locale?: string },
): React.ReactNode {
  if (cell === null || cell === undefined || cell === '') return missingCell(options.emptyLabel);
  if (typeof cell === 'string' || typeof cell === 'number' || typeof cell === 'boolean') {
    return cell;
  }
  if (!isFmTableStructuredCell(cell)) return missingCell(options.emptyLabel);
  if (cell.kind === 'signed' && cell.value === null) return missingCell(options.emptyLabel);
  if (cell.kind === 'signed') {
    return (
      <FmDataValue
        rule={signedRule(cell, options.locale)}
        locale={options.locale}
        unavailableLabel={options.emptyLabel}
      />
    );
  }
  if (cell.kind === 'species') {
    return (
      <FmInline as="span" gap="xs" align="center" wrap={false}>
        <ColorSwatch
          color={cell.speciesCode == null ? 'var(--fm-d-border)' : paletteSpecies(cell.speciesCode)}
          size={10}
        />
        <FmText as="span" size="fine" truncate>
          {cell.label}
        </FmText>
      </FmInline>
    );
  }
  return <FmTag hue={OPERATION_TAG_HUE[cell.operation]} label={cell.label} />;
}
